import { CirclePlus, Trash2, X } from "lucide-react";
import ButtonV2 from "../../../../components/ui/button/v2";
import { OptionNameValuePair } from "../../../../lib/interfaces/types";

import {
  PrimaryParty,
  AccountRelations,
} from "../../../../api/creditAccounts";
import { buildEmptyAccountRelations } from "../../utils/creditFactories";
import { getPartyErrors } from "../../utils/creditValidation";
import FormField, { useTouched } from "./FormField";
import { RelatedAs } from "../../../../api/stakeholder_relationships";
import { PartyRelationRow } from "./PartyRelationRow";

interface PartyRowProps {
  party: PrimaryParty;
  all: PrimaryParty[];
  accountOptions: OptionNameValuePair<string>[];
  showErrors: boolean;
  onChange: (party: PrimaryParty) => void;
  onDelete: () => void;
}

const PartyRow: React.FC<PartyRowProps> = ({
  party,
  all,
  accountOptions,
  showErrors,
  onChange,
  onDelete,
}) => {
  const { isTouched, markTouched } = useTouched();
  const errors = getPartyErrors(party, all);
  const show = (key: string) => showErrors || isTouched(key);
  const hasNameError = Boolean(show("name") && errors.name);

  const isPrimaryBorrowerParty = party.account_relationships.some(
    (r) => r.related_as === RelatedAs.PRIMARY_BORROWER
  );

  const changeRelation = (id: string, patch: Partial<AccountRelations>) => {
    onChange({
      ...party,
      account_relationships: party.account_relationships.map((r) =>
        r.id === id ? { ...r, ...patch } : r
      ),
    });
  }
    
  const addRelation = () => {
    onChange({
      ...party,
      account_relationships: [...party.account_relationships, buildEmptyAccountRelations()],
    });
  }
    
  const removeRelation = (id: string) => {
    onChange({
      ...party,
      account_relationships: party.account_relationships.filter((r) => r.id !== id),
    });
  }

  return (
    <div className="flex flex-col gap-3 rounded-md border border-gray-100 bg-white p-3">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
        <div className="group relative">
          <FormField
            type="text"
            label="Party Name"
            required
            disabled={isPrimaryBorrowerParty}
            placeholder="Enter name"
            value={party.name}
            error={show("name") ? errors.name : undefined}
            onChange={(v) => onChange({ ...party, name: v })}
            onBlur={() => markTouched("name")}
          />
          {isPrimaryBorrowerParty && (
            <div className="pointer-events-none absolute left-0 top-full z-50 mt-1 hidden w-max max-w-[400px] rounded-[6px] bg-[#1F2937] px-3 py-2 text-xs text-white shadow-lg group-hover:block">
              Account Name already provided in Loan Accounts. To edit, please change the Account Name in the Loan Accounts Section
            </div>
          )}
        </div>
        <div className="flex items-center pt-5">
          <ButtonV2
            variant="outline"
            size="sm"
            onClick={onDelete}
            title="Delete party"
            className="h-9 px-2 rounded-md bg-white border border-gray-300 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500 disabled:opacity-40"
            prefix={<Trash2 width="16" height="16" />}
          />
        </div>
      </div>

      <div className="relative flex flex-col gap-3 pl-[20px]">
        {party.account_relationships.map((relations, index) => (
          <PartyRelationRow 
            key={relations.id}
            relations={relations}
            allRelations={party.account_relationships}
            accountOptions={accountOptions}
            onChange={(patch) => {changeRelation(relations.id, patch)}}
            onDelete={() => {removeRelation(relations.id)}}
            showErrors={showErrors}
            relationError={errors.relations[relations.id]}
            accountNumbersError={errors.account_numbers[relations.id]}
            isFirst={index === 0}
            isLast={index === party.account_relationships.length - 1}
            parentHasError={hasNameError}
          />
        ))}
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-gray-500 self-start hover:text-[#3551F3]"
          onClick={addRelation}
        >
          <CirclePlus width={14} height={14} /> Add another
        </button>
      </div>
    </div>
  );
};

export default PartyRow;